/**
 * A very small Markdown renderer, for assistant messages.
 *
 * The phone renders Sensei's replies with react-native-markdown-display. The
 * web had no equivalent and no markdown dependency, so replies would otherwise
 * show their raw asterisks and hashes.
 *
 * This covers the subset that actually turns up in those replies -- headings,
 * bullet and numbered lists, bold, italic, inline code, and paragraphs -- and
 * nothing else. It is deliberately not a Markdown implementation: anything it
 * does not recognise is rendered as the plain text it is, which is the right
 * failure for a chat message.
 *
 * Everything is built as React elements. There is no dangerouslySetInnerHTML
 * anywhere in here, and there must not be: this renders text that came back
 * from a model, which is not ours to trust as markup.
 */
import React from 'react';

/** Inline spans: **bold**, *italic*, `code`. */
const inline = (text: string, keyBase: string): React.ReactNode[] => {
  const out: React.ReactNode[] = [];
  // One pass, longest markers first, so ** is not eaten by *.
  const pattern = /(\*\*[^*]+\*\*|`[^`]+`|\*[^*]+\*|_[^_]+_)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;

  // eslint-disable-next-line no-cond-assign
  while ((m = pattern.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const tok = m[0];
    const key = `${keyBase}-i${i += 1}`;

    if (tok.startsWith('**')) {
      out.push(<strong key={key} className="font-extrabold">{tok.slice(2, -2)}</strong>);
    } else if (tok.startsWith('`')) {
      out.push(
        <code key={key} className="px-1 py-0.5 rounded bg-black/20 text-[0.92em] font-mono">
          {tok.slice(1, -1)}
        </code>,
      );
    } else {
      out.push(<em key={key}>{tok.slice(1, -1)}</em>);
    }
    last = m.index + tok.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
};

const SimpleMarkdown: React.FC<{ text: string; className?: string }> = ({ text, className = '' }) => {
  const lines = (text || '').split('\n');
  const blocks: React.ReactNode[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;
  let para: string[] = [];
  let n = 0;

  const flushList = () => {
    if (!list) return;
    const items = list.items.map((it, i) => (
      <li key={`li-${n}-${i}`} className="ml-4 pl-1">{inline(it, `li${n}${i}`)}</li>
    ));
    blocks.push(list.ordered
      ? <ol key={`ol-${n += 1}`} className="list-decimal space-y-1 my-2">{items}</ol>
      : <ul key={`ul-${n += 1}`} className="list-disc space-y-1 my-2">{items}</ul>);
    list = null;
  };

  const flushPara = () => {
    if (para.length === 0) return;
    const joined = para.join(' ');
    blocks.push(<p key={`p-${n += 1}`} className="my-2 first:mt-0 last:mb-0">{inline(joined, `p${n}`)}</p>);
    para = [];
  };

  lines.forEach((raw) => {
    const line = raw.trimEnd();

    if (!line.trim()) { flushList(); flushPara(); return; }

    const heading = line.match(/^(#{1,3})\s+(.*)$/);
    if (heading) {
      flushList(); flushPara();
      const size = heading[1].length === 1 ? 'text-[17px]' : heading[1].length === 2 ? 'text-[15.5px]' : 'text-[14.5px]';
      blocks.push(
        <p key={`h-${n += 1}`} className={`${size} font-black tracking-[-0.2px] mt-3 mb-1 first:mt-0`}>
          {inline(heading[2], `h${n}`)}
        </p>,
      );
      return;
    }

    const bullet = line.match(/^\s*[-*]\s+(.*)$/);
    const numbered = line.match(/^\s*\d+[.)]\s+(.*)$/);
    if (bullet || numbered) {
      flushPara();
      const ordered = !!numbered;
      if (!list || list.ordered !== ordered) { flushList(); list = { ordered, items: [] }; }
      list.items.push((bullet ? bullet[1] : numbered![1]));
      return;
    }

    flushList();
    para.push(line.trim());
  });

  flushList();
  flushPara();

  return <div className={className}>{blocks}</div>;
};

export default SimpleMarkdown;
